import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Menu, X, Plus, MessageSquare, User, LogOut, Shield, Package, Search } from 'lucide-react';
import api from '@/lib/api';

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user && user.user_id) {
      api.get('/messages/unread-count').then(r => setUnreadCount(r.data.count)).catch(() => {});
      const interval = setInterval(() => {
        api.get('/messages/unread-count').then(r => setUnreadCount(r.data.count)).catch(() => {});
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-[#E8E6DF] sticky top-0 z-50" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <div className="w-8 h-8 bg-primary rounded-xl flex items-center justify-center">
              <Package className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-semibold text-xl tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              BorrowTools
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <Link to="/browse" data-testid="header-nav-browse">
              <Button variant={isActive('/browse') ? 'secondary' : 'ghost'} size="sm" className="rounded-full gap-1.5">
                <Search className="w-4 h-4" strokeWidth={1.5} />Browse
              </Button>
            </Link>
            <Link to="/browse?category=sale" data-testid="header-nav-sale">
              <Button variant="ghost" size="sm" className="rounded-full">For Sale</Button>
            </Link>
            <Link to="/browse?category=free" data-testid="header-nav-free">
              <Button variant="ghost" size="sm" className="rounded-full">Free</Button>
            </Link>
            <Link to="/browse?category=lending" data-testid="header-nav-lending">
              <Button variant="ghost" size="sm" className="rounded-full">Lending</Button>
            </Link>
          </nav>

          <div className="hidden md:flex items-center gap-2">
            {user && user.user_id ? (
              <>
                <Link to="/create-listing" data-testid="header-create-listing">
                  <Button className="rounded-full gap-1.5 bg-primary hover:bg-primary/90 text-white">
                    <Plus className="w-4 h-4" strokeWidth={1.5} />Post Listing
                  </Button>
                </Link>
                <Link to="/messages" data-testid="header-nav-messages" className="relative">
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <MessageSquare className="w-5 h-5" strokeWidth={1.5} />
                  </Button>
                  {unreadCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-[10px] bg-destructive text-white rounded-full">
                      {unreadCount}
                    </Badge>
                  )}
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild data-testid="header-user-menu">
                    <Button variant="ghost" size="icon" className="rounded-full">
                      {user.picture ? (
                        <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <User className="w-5 h-5" strokeWidth={1.5} />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <div className="px-2 py-1.5 text-sm font-medium truncate">{user.name || user.email}</div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(`/profile/${user.user_id}`)} data-testid="menu-profile">
                      <User className="w-4 h-4 mr-2" strokeWidth={1.5} />Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/my-listings')} data-testid="menu-my-listings">
                      <Package className="w-4 h-4 mr-2" strokeWidth={1.5} />My Listings
                    </DropdownMenuItem>
                    {user.role === 'admin' && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                        <Shield className="w-4 h-4 mr-2" strokeWidth={1.5} />Admin Panel
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="header-login-btn">
                  <Button variant="ghost" className="rounded-full">Sign In</Button>
                </Link>
                <Link to="/register" data-testid="header-signup-btn">
                  <Button className="rounded-full bg-primary hover:bg-primary/90 text-white">Sign Up</Button>
                </Link>
              </>
            )}
          </div>

          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-4 animate-fade-in">
            <div className="flex flex-col gap-1">
              <Link to="/browse" onClick={() => setMobileOpen(false)}>
                <Button variant="ghost" className="w-full justify-start rounded-xl">Browse Items</Button>
              </Link>
              <Link to="/browse?category=sale" onClick={() => setMobileOpen(false)}>
                <Button variant="ghost" className="w-full justify-start rounded-xl">For Sale</Button>
              </Link>
              <Link to="/browse?category=free" onClick={() => setMobileOpen(false)}>
                <Button variant="ghost" className="w-full justify-start rounded-xl">Free</Button>
              </Link>
              <Link to="/browse?category=lending" onClick={() => setMobileOpen(false)}>
                <Button variant="ghost" className="w-full justify-start rounded-xl">Lending</Button>
              </Link>
              {user && user.user_id ? (
                <>
                  <Link to="/create-listing" onClick={() => setMobileOpen(false)}>
                    <Button className="w-full rounded-xl bg-primary text-white mt-2">Post Listing</Button>
                  </Link>
                  <Link to="/messages" onClick={() => setMobileOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start rounded-xl">
                      Messages {unreadCount > 0 && `(${unreadCount})`}
                    </Button>
                  </Link>
                  <Link to={`/profile/${user.user_id}`} onClick={() => setMobileOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start rounded-xl">Profile</Button>
                  </Link>
                  <Link to="/my-listings" onClick={() => setMobileOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start rounded-xl">My Listings</Button>
                  </Link>
                  {user.role === 'admin' && (
                    <Link to="/admin" onClick={() => setMobileOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start rounded-xl">Admin</Button>
                    </Link>
                  )}
                  <Button variant="ghost" className="w-full justify-start rounded-xl text-destructive" onClick={handleLogout}>
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)}>
                    <Button variant="ghost" className="w-full rounded-xl mt-2">Sign In</Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileOpen(false)}>
                    <Button className="w-full rounded-xl bg-primary text-white">Sign Up</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
